import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { DeleteIcon, PlusIcon, TeacherIcon, UserRemoveIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { assignCenterTefl } from '../../../../api/centers/center-tefl/assign-center-tefl'
import { unassignCenterTefl } from '../../../../api/centers/center-tefl/unassign-center-tefl'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterTefl } from '../../../../lib/types/models/center-tefl/center-tefl'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import CenterTeflForm from '../../ol-common/ol-center-program/ol-center-tefl/center-tefl-form'
import OlCenterTeflList from '../../ol-common/ol-center-program/ol-center-tefl/ol-center-tefl-list'
import { centerTeflColumns } from '../../ol-common/ol-center-program/ol-center-tefl/utils'

type Props = {
  centerId: number
  olId?: number
}

const OlPersonalSpaceCenterTeflList = ({ centerId, olId }: Props) => {
  const { navigate } = useNavigator()
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'delete',
    CenterTefl
  >(['create', 'delete'])

  const assignTeflMutation = useMutation({
    mutationFn: assignCenterTefl,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('TEFL assigned successfully')
      queryClient.invalidateQueries({ queryKey: ['center-tefls', centerId] })
      closeModel('create')
    }
  })

  const unassignTeflMutation = useMutation({
    mutationFn: unassignCenterTefl,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('TEFL unassigned successfully')
      queryClient.invalidateQueries({ queryKey: ['center-tefls', centerId] })
    }
  })

  return (
    <div>
      <OlCenterTeflList
        centerId={centerId}
        olId={olId}
        columns={[
          {
            accessorFn: (row) => row.tefl?.lastName,
            accessorKey: 'tefl.lastName',
            header: t('ol.center.tefl.table.lastName'),
            cell: ({ row }) => {
              const tefl = row.original.tefl

              return tefl && olId ? (
                <Typography.Link
                  variant="secondary-body"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(
                      Paths.OL_TEFL_UPDATE_PAGE.replace(':olId', olId.toString()).replace(
                        ':id',
                        tefl.id.toString()
                      )
                    )
                  }}
                >
                  {row.original.tefl?.lastName.toUpperCase()}
                </Typography.Link>
              ) : (
                <Button
                  label="Add TEFL"
                  icon={PlusIcon}
                  iconSize={12}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleModal('create', row.original)
                  }}
                />
              )
            }
          },
          ...centerTeflColumns(t, false),
          {
            accessorKey: 'id',
            header: '',
            cell: ({ row }) => (
              <ActionButton
                variant={'danger'}
                icon={DeleteIcon}
                disabled={!row.original.tefl}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('delete', row.original)
                }}
              />
            )
          }
        ]}
      />
      {selectedData && olId && (
        <Modal
          isOpen={modalState.create}
          title={'Add TEFL'}
          size="medium"
          hideActions
          icon={() => <TeacherIcon color={tailwindColors.primary.DEFAULT} />}
          onCancel={() => closeModel('create')}
        >
          <CenterTeflForm
            olId={olId}
            qualification={selectedData.qualification}
            className={MODAL_CONTENT_CLASSNAME}
            defaultValues={{ id: selectedData.id }}
            buttonsConfig={modalButtonConfig({
              cancelText: 'Close',
              onCancel: () => closeModel('create'),
              confirmText: 'Confirm',
              isLoading: assignTeflMutation.isPending,
              isEnglish: true,
              onConfirm: async (values) => {
                await assignTeflMutation.mutateAsync({
                  centerId,
                  id: selectedData.id,
                  teflId: values.teflId
                })
              }
            })}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState.delete}
          icon={UserRemoveIcon}
          variant="destructive"
          size="small"
          title="Are you sure you want to unassign this TEFL?"
          hasBorderBottom={false}
          isLoading={unassignTeflMutation.isPending}
          onCancel={() => closeModel('delete')}
          actionButtonsOptions={{
            confirmText: 'Unassign',
            cancelText: 'Cancel',
            onConfirm: async () => {
              await unassignTeflMutation.mutateAsync({ centerId, id: selectedData.id })
              closeModel('delete')
            }
          }}
        />
      )}
    </div>
  )
}

export default OlPersonalSpaceCenterTeflList
